import {
  Alert,
  Button,
  Empty,
  Input,
  InputNumber,
  Message,
  Modal,
  Pagination,
  Radio,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
} from "@arco-design/web-react";
import type { TableColumnProps } from "@arco-design/web-react";
import {
  ArrowDown,
  ArrowUp,
  ClipboardCheck,
  Eye,
  Pencil,
  RotateCcw,
  Search,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import {
  itineraryListMobilePageSize,
  itineraryListMobileScopes,
  itineraryListPageSizeOptions,
} from "../config";
import {
  isIgnoredItineraryStayName,
  useItineraryListCandidateSearch,
  useItineraryListRuntime,
  useItineraryStaffUserCandidates,
  type ItineraryListCandidateKind,
  type ItineraryListNavigationState,
} from "../runtime";
import { formatItineraryCreatedAt, resolveItineraryStatus } from "../service";
import type {
  FinalizedBudgetQuoteVersion,
  ItineraryListItem,
  ItineraryListSortBy,
  ItineraryListSortOrder,
  ItineraryScope,
  ItinerarySalesNotificationResult,
  StaffUserCandidate,
} from "../types";
import "./ItineraryListPage.css";

const ITINERARY_TABLE_SCROLL_WIDTH = 2346;

type Props = {
  onPreview: (itineraryId: string, readOnly: boolean) => void;
  canQuote: boolean;
  onQuote: (itineraryId: string, budgetOrderId?: string) => void;
  canCustomerQuote: boolean;
  canCreateCustomerQuote?: boolean;
  onLoadFinalizedBudgetQuoteVersions: (itineraryId: string) => Promise<FinalizedBudgetQuoteVersion[]>;
  onCustomerQuote: (budgetOrderId: string, customerQuoteId?: string) => void;
  canOrderHandover: boolean;
  canTicketRefund?: boolean;
  onTicketRefund?: (teamId: string) => void;
  canFulfillment: boolean;
  onFulfillment: (itineraryId: string) => void;
  canEdit: boolean;
  onEdit: (itineraryId: string, listState: ItineraryListNavigationState) => void;
  canPreview?: boolean;
  canExport: boolean;
  onDownloadPdf: (itineraryId: string, includeDayImages: boolean) => Promise<void> | void;
  canNotifySales: boolean;
  onNotifySales: (projectId: string, itineraryId: string) => Promise<ItinerarySalesNotificationResult>;
  initialState?: ItineraryListNavigationState;
  onNavigationStateChange?: (listState: ItineraryListNavigationState) => void;
};

type FinalizedBudgetQuoteVersionState = {
  loading: boolean;
  versions: FinalizedBudgetQuoteVersion[] | null;
  errorMessage: string;
};

const EMPTY_FINALIZED_BUDGET_QUOTE_VERSION_STATE: FinalizedBudgetQuoteVersionState = {
  loading: false,
  versions: null,
  errorMessage: "",
};

/** 课程线路管理页面主体。 */
export function ItineraryListFeaturePage({
  onPreview,
  canQuote,
  onQuote,
  canCustomerQuote,
  canCreateCustomerQuote = canCustomerQuote,
  onLoadFinalizedBudgetQuoteVersions,
  onCustomerQuote,
  canOrderHandover,
  canFulfillment,
  onFulfillment,
  canTicketRefund,
  onTicketRefund,
  canEdit,
  onEdit,
  canPreview = true,
  canExport,
  onDownloadPdf,
  canNotifySales,
  onNotifySales,
  initialState,
  onNavigationStateChange,
}: Props) {
  const runtime = useItineraryListRuntime(initialState);
  const isMobileViewport = useIsMobileViewport();
  const salesUserCandidates = useItineraryStaffUserCandidates("sales");
  const dispatcherUserCandidates = useItineraryStaffUserCandidates("dispatcher");
  // 范围切换沿用旧列表时不能把旧记录当作本人负责行程开放操作。
  const canOperate = runtime.scope === "mine" && !runtime.loading && !runtime.errorMessage;
  const topScrollbarRef = useRef<HTMLDivElement>(null);
  const tableWrapperRef = useRef<HTMLDivElement>(null);
  const syncingScrollRef = useRef(false);
  const [pdfDownloadingId, setPdfDownloadingId] = useState<string>();
  const [pdfErrorMessage, setPdfErrorMessage] = useState("");
  const [budgetEntryError, setBudgetEntryError] = useState("");
  const [budgetEntryItineraryId, setBudgetEntryItineraryId] = useState<string | null>(null);
  const [expandedQuoteRowKeys, setExpandedQuoteRowKeys] = useState<string[]>([]);
  const [finalizedBudgetQuoteVersionStates, setFinalizedBudgetQuoteVersionStates] = useState<
    Record<string, FinalizedBudgetQuoteVersionState>
  >({});

  useEffect(() => {
    onNavigationStateChange?.(runtime.navigationState);
  }, [onNavigationStateChange, runtime.navigationState]);

  /** 窄屏默认每页 5 条，避免卡片列表一次加载过多。 */
  useEffect(() => {
    if (!isMobileViewport) return;
    if (runtime.pageSize === itineraryListMobilePageSize) return;
    runtime.handlePageSizeChange(itineraryListMobilePageSize);
  }, [isMobileViewport, runtime.pageSize]);

  /** 移动端不提供全部行程/我的团，非法范围回落到我的行程。 */
  useEffect(() => {
    if (!isMobileViewport) return;
    if ((itineraryListMobileScopes as readonly string[]).includes(runtime.scope)) return;
    runtime.handleScopeChange("mine");
  }, [isMobileViewport, runtime.scope]);

  const findTableScroller = useCallback(() => (
    tableWrapperRef.current?.querySelector<HTMLElement>(".arco-table-content-inner")
    ?? tableWrapperRef.current?.querySelector<HTMLElement>(".arco-table-body")
    ?? tableWrapperRef.current?.querySelector<HTMLElement>(".arco-table-content-scroll")
    ?? null
  ), []);

  const syncScrollLeft = useCallback((source: HTMLElement, target: HTMLElement) => {
    if (syncingScrollRef.current || target.scrollLeft === source.scrollLeft) return;
    syncingScrollRef.current = true;
    target.scrollLeft = source.scrollLeft;
    syncingScrollRef.current = false;
  }, []);

  const handleTopScroll = useCallback(() => {
    const topScrollbar = topScrollbarRef.current;
    const tableScroller = findTableScroller();
    if (topScrollbar && tableScroller) syncScrollLeft(topScrollbar, tableScroller);
  }, [findTableScroller, syncScrollLeft]);

  useEffect(() => {
    const topScrollbar = topScrollbarRef.current;
    const tableScroller = findTableScroller();
    if (!topScrollbar || !tableScroller) return undefined;
    const handleTableScroll = () => syncScrollLeft(tableScroller, topScrollbar);
    tableScroller.addEventListener("scroll", handleTableScroll);
    topScrollbar.scrollLeft = tableScroller.scrollLeft;
    return () => tableScroller.removeEventListener("scroll", handleTableScroll);
  }, [findTableScroller, runtime.items.length, runtime.loading, syncScrollLeft]);

  const downloadPdf = async (itineraryId: string, includeDayImages: boolean) => {
    if (pdfDownloadingId) return;
    setPdfDownloadingId(itineraryId);
    setPdfErrorMessage("");
    try {
      await onDownloadPdf(itineraryId, includeDayImages);
    } catch {
      setPdfErrorMessage("PDF 下载失败，请稍后重试");
    } finally {
      setPdfDownloadingId(undefined);
    }
  };

  const canExpandFinalizedBudgetQuote = useCallback((item: ItineraryListItem) => (
    Boolean(
      canOperate && canCustomerQuote
      && (item.finalizedBudgetCount > 0 || item.finalizedQuoteCount > 0),
    )
  ), [canOperate, canCustomerQuote]);

  const toggleFinalizedBudgetQuoteRow = useCallback((item: ItineraryListItem) => {
    if (!canExpandFinalizedBudgetQuote(item)) return;
    const isExpanded = expandedQuoteRowKeys.includes(item.itineraryId);
    setExpandedQuoteRowKeys((current) => (
      current.includes(item.itineraryId)
        ? current.filter((key) => key !== item.itineraryId)
        : [...current, item.itineraryId]
    ));
    const state = finalizedBudgetQuoteVersionStates[item.itineraryId];
    if (isExpanded || (state && !state.errorMessage)) return;

    setFinalizedBudgetQuoteVersionStates((current) => ({
      ...current,
      [item.itineraryId]: {
        loading: true,
        versions: null,
        errorMessage: "",
      },
    }));
    void onLoadFinalizedBudgetQuoteVersions(item.itineraryId)
      .then((versions) => {
        setFinalizedBudgetQuoteVersionStates((current) => ({
          ...current,
          [item.itineraryId]: {
            loading: false,
            versions,
            errorMessage: "",
          },
        }));
      })
      .catch(() => {
        setFinalizedBudgetQuoteVersionStates((current) => ({
          ...current,
          [item.itineraryId]: {
            loading: false,
            versions: null,
            errorMessage: "定稿核价列表加载失败，请稍后重试。",
          },
        }));
      });
  }, [
    canExpandFinalizedBudgetQuote,
    expandedQuoteRowKeys,
    finalizedBudgetQuoteVersionStates,
    onLoadFinalizedBudgetQuoteVersions,
  ]);

  const openQuoteBudget = useCallback((item: ItineraryListItem) => {
    if (item.finalizedBudgetCount <= 0) {
      onQuote(item.itineraryId);
      return;
    }
    const cached = finalizedBudgetQuoteVersionStates[item.itineraryId]?.versions;
    setBudgetEntryError("");
    setBudgetEntryItineraryId(item.itineraryId);
    void (cached ? Promise.resolve(cached) : onLoadFinalizedBudgetQuoteVersions(item.itineraryId))
      .then((versions) => {
        const latest = versions[0];
        if (!latest?.budgetOrderId) {
          setBudgetEntryError("未找到已定稿核价版本，请稍后重试。");
          return;
        }
        onQuote(item.itineraryId, latest.budgetOrderId);
      })
      .catch(() => setBudgetEntryError("定稿核价打开失败，请稍后重试。"))
      .finally(() => setBudgetEntryItineraryId(null));
  }, [finalizedBudgetQuoteVersionStates, onLoadFinalizedBudgetQuoteVersions, onQuote]);

  const columns = buildItineraryColumns({
    onPreview: (itineraryId) => onPreview(itineraryId, !canOperate),
    canQuote: canOperate && canQuote,
    onQuote,
    canCustomerQuote: canOperate && canCustomerQuote,
    canCreateCustomerQuote: canOperate && canCreateCustomerQuote,
    onCustomerQuote,
    canOrderHandover: canOperate && canOrderHandover,
    canTicketRefund: runtime.scope === "mine_formed" && !runtime.loading && !runtime.errorMessage && canTicketRefund,
    onTicketRefund,
    canFulfillment: canOperate && canFulfillment,
    onFulfillment,
    canEdit: canOperate && canEdit,
    onEdit,
    canPreview,
    canExport,
    onDownloadPdf: downloadPdf,
    canNotifySales: canOperate && canNotifySales,
    onNotifySales,
    pdfDownloadingId,
    listState: runtime.navigationState,
    sortBy: runtime.sortBy,
    sortOrder: runtime.sortOrder,
    onSort: runtime.handleSort,
    expandedQuoteRowKeys,
    onToggleFinalizedBudgetQuoteRow: toggleFinalizedBudgetQuoteRow,
    onOpenQuoteBudget: openQuoteBudget,
    budgetEntryItineraryId,
  });

  if (isMobileViewport) {
    return (
      <main className="itinerary-list-page is-mobile" data-testid="itinerary-list-mobile-page">
        <MobileItineraryFilterBar runtime={runtime} />
        <MobileItinerarySortBar
          sortBy={runtime.sortBy}
          sortOrder={runtime.sortOrder}
          onSort={runtime.handleSort}
        />
        {runtime.itineraryIdError ? (
          <div id="itinerary-list-id-error" className="itinerary-list-filter-error" role="alert">
            {runtime.itineraryIdError}
          </div>
        ) : null}
        {runtime.errorMessage ? (
          <Alert
            type="error"
            showIcon
            content={runtime.errorMessage}
            action={<Button size="small" onClick={runtime.retry}>重试</Button>}
          />
        ) : null}
        <MobileItineraryList
          runtime={runtime}
          onPreview={(itineraryId) => onPreview(itineraryId, runtime.scope !== "mine")}
        />
      </main>
    );
  }

  return (
    <main className="itinerary-list-page">
      <section className="itinerary-list-filter-panel" aria-label="课程线路筛选">
        <div className="itinerary-list-filter-body" data-layout="two-tier-responsive">
          <div className="itinerary-list-filter-grid">
          <Input
            allowClear
            aria-label="行程 UUID 或链接"
            aria-invalid={Boolean(runtime.itineraryIdError)}
            aria-describedby={runtime.itineraryIdError ? "itinerary-list-id-error" : undefined}
            value={runtime.draftItineraryIdInput}
            status={runtime.itineraryIdError ? "error" : undefined}
            placeholder="行程 UUID 或包含 UUID 的链接"
            onChange={runtime.setDraftItineraryIdInput}
            onPressEnter={runtime.handleSearch}
          />
          <Input
            allowClear
            aria-label="关键词筛选"
            value={runtime.draftKeyword}
            maxLength={{ length: 100, errorOnly: true }}
            placeholder="标题、定制号或团编码"
            onChange={runtime.setDraftKeyword}
            onPressEnter={runtime.handleSearch}
          />
          <Input
            allowClear
            aria-label="学校筛选"
            value={runtime.draftSchoolName}
            maxLength={{ length: 100, errorOnly: true }}
            placeholder="学校"
            onChange={runtime.setDraftSchoolName}
            onPressEnter={runtime.handleSearch}
          />
          <Select
            allowClear
            showSearch
            aria-label="销售筛选"
            placeholder="销售"
            value={runtime.draftSalesUserId || undefined}
            loading={salesUserCandidates.loading}
            filterOption={(inputValue, option) => {
              const label = String(option?.props?.children ?? "");
              return label.toLowerCase().includes(inputValue.trim().toLowerCase());
            }}
            onChange={(value) => {
              const userId = value == null ? undefined : String(value);
              const selected = mergeStaffUserOptions(
                salesUserCandidates.options,
                runtime.draftSalesUserId,
                runtime.draftSalesUserName,
              ).find((item) => item.userId === userId);
              runtime.setDraftSalesUser(userId, selected?.displayName);
            }}
          >
            {mergeStaffUserOptions(
              salesUserCandidates.options,
              runtime.draftSalesUserId,
              runtime.draftSalesUserName,
            ).map((item) => (
              <Select.Option key={item.userId} value={item.userId}>
                {item.displayName}
              </Select.Option>
            ))}
          </Select>
          <Select
            allowClear
            showSearch
            aria-label="计调筛选"
            placeholder="计调"
            value={runtime.draftDispatcherUserId || undefined}
            loading={dispatcherUserCandidates.loading}
            filterOption={(inputValue, option) => {
              const label = String(option?.props?.children ?? "");
              return label.toLowerCase().includes(inputValue.trim().toLowerCase());
            }}
            onChange={(value) => {
              const userId = value == null ? undefined : String(value);
              const selected = mergeStaffUserOptions(
                dispatcherUserCandidates.options,
                runtime.draftDispatcherUserId,
                runtime.draftDispatcherUserName,
              ).find((item) => item.userId === userId);
              runtime.setDraftDispatcherUser(userId, selected?.displayName);
            }}
          >
            {mergeStaffUserOptions(
              dispatcherUserCandidates.options,
              runtime.draftDispatcherUserId,
              runtime.draftDispatcherUserName,
            ).map((item) => (
              <Select.Option key={item.userId} value={item.userId}>
                {item.displayName}
              </Select.Option>
            ))}
          </Select>
          <MultiSelectFilter
            label="专业筛选"
            placeholder="专业"
            kind="major"
            values={runtime.draftAdvancedFilters.majorNames}
            onChange={(majorNames) => runtime.setDraftAdvancedFilters({ majorNames })}
          />
          <InputNumber
            aria-label="行程天数筛选"
            min={1}
            max={365}
            precision={0}
            placeholder="天数"
            value={runtime.draftAdvancedFilters.durationDays}
            onChange={(durationDays) => runtime.setDraftAdvancedFilters({ durationDays })}
          />
          <MultiSelectFilter
            label="省份筛选"
            placeholder="省份"
            kind="province"
            values={runtime.draftAdvancedFilters.provinceNames}
            onChange={(provinceNames) => runtime.setDraftAdvancedFilters({ provinceNames })}
          />
          <MultiSelectFilter
            label="住宿点筛选"
            placeholder="住宿点"
            kind="stay_area"
            values={runtime.draftAdvancedFilters.stayAreaNames}
            onChange={(stayAreaNames) => runtime.setDraftAdvancedFilters({ stayAreaNames })}
          />
          <MultiSelectFilter
            label="考察点筛选"
            placeholder="考察点"
            kind="point"
            values={runtime.draftAdvancedFilters.pointNames}
            provinceNames={runtime.draftAdvancedFilters.provinceNames}
            onChange={(pointNames) => runtime.setDraftAdvancedFilters({ pointNames })}
          />
          </div>
          <div className="itinerary-list-filter-actions">
            <Button icon={<RotateCcw size={16} />} onClick={runtime.handleReset}>
              重置
            </Button>
            <Button
              type="primary"
              icon={<Search size={16} />}
              loading={runtime.loading}
              disabled={runtime.loading}
              aria-busy={runtime.loading}
              onClick={runtime.handleSearch}
            >
              查询
            </Button>
          </div>
        </div>
      </section>

      {runtime.itineraryIdError ? (
        <div id="itinerary-list-id-error" className="itinerary-list-filter-error" role="alert">
          {runtime.itineraryIdError}
        </div>
      ) : null}
      {runtime.errorMessage ? (
        <Alert
          type="error"
          showIcon
          content={runtime.errorMessage}
          action={<Button size="small" onClick={runtime.retry}>重试</Button>}
        />
      ) : null}
      {pdfErrorMessage ? <Alert type="error" showIcon content={pdfErrorMessage} /> : null}
      {budgetEntryError ? <Alert type="error" showIcon content={budgetEntryError} /> : null}

      <section className="itinerary-list-table-panel" aria-label="课程线路结果">
        <div className="itinerary-list-section-header">
          <div className="itinerary-list-title-row">
            <h2>行程列表</h2>
            <Radio.Group
              type="button"
              aria-label="行程范围"
              value={runtime.scope}
              onChange={(value) => {
                setExpandedQuoteRowKeys([]);
                runtime.handleScopeChange(value as ItineraryScope);
              }}
            >
              <Radio value="mine">我的行程</Radio>
              <Radio value="recent_3y">近三年行程</Radio>
              <Radio value="all">全部行程</Radio>
              <Radio value="mine_formed">我的团</Radio>
            </Radio.Group>
          </div>
          <div className="itinerary-list-table-actions">
            <div className="itinerary-list-statline">
              {runtime.loading && runtime.total === 0 ? "正在加载" : `共 ${runtime.total} 条`}
            </div>
          </div>
        </div>
        <div
          ref={topScrollbarRef}
          className="itinerary-list-top-scrollbar"
          data-testid="itinerary-list-top-scrollbar"
          aria-label="课程线路顶部横向滚动条"
          onScroll={handleTopScroll}
        >
          <div
            className="itinerary-list-top-scrollbar-inner"
            style={{ width: ITINERARY_TABLE_SCROLL_WIDTH }}
          />
        </div>
        <div ref={tableWrapperRef}>
          <Table
            rowKey="itineraryId"
            columns={columns}
            data={runtime.items}
            loading={runtime.loading}
            expandedRowKeys={canOperate && canCustomerQuote ? expandedQuoteRowKeys : []}
            expandedRowRender={(item) => (
              canOperate && canCustomerQuote ? <FinalizedBudgetQuoteExpandedRow
                state={finalizedBudgetQuoteVersionStates[item.itineraryId]
                  ?? EMPTY_FINALIZED_BUDGET_QUOTE_VERSION_STATE}
                canCustomerQuote={canCustomerQuote}
                canCreateCustomerQuote={canCreateCustomerQuote}
                onCustomerQuote={onCustomerQuote}
              /> : null
            )}
            expandProps={{
              width: 0,
              icon: () => null,
              columnTitle: null,
              rowExpandable: canExpandFinalizedBudgetQuote,
            }}
            border={{ wrapper: true, cell: false }}
            scroll={{ x: ITINERARY_TABLE_SCROLL_WIDTH }}
            noDataElement={runtime.loading
              ? <span role="status">正在加载课程线路</span>
              : <Empty description="暂无行程" />}
            pagination={{
              current: runtime.page,
              pageSize: runtime.pageSize,
              total: runtime.total,
              sizeCanChange: true,
              sizeOptions: itineraryListPageSizeOptions,
              selectProps: { "aria-label": "每页行程数量" } as Record<string, string>,
              showTotal: true,
              onChange: runtime.handlePageChange,
              onPageSizeChange: runtime.handlePageSizeChange,
            }}
          />
        </div>
      </section>
    </main>
  );
}

/** 把已选销售/计调并入候选，避免回填后只显示用户 ID。 */
function mergeStaffUserOptions(
  options: StaffUserCandidate[],
  userId?: string,
  displayName?: string,
): StaffUserCandidate[] {
  const selectedId = userId?.trim();
  if (!selectedId) return options;
  if (options.some((item) => item.userId === selectedId)) return options;
  return [
    { userId: selectedId, displayName: displayName?.trim() || "已选人员" },
    ...options,
  ];
}

/** 可创建的多值筛选输入。 */
function MultiSelectFilter({
  label,
  placeholder,
  kind,
  values,
  provinceNames = [],
  onChange,
}: {
  label: string;
  placeholder: string;
  kind: ItineraryListCandidateKind;
  values: string[];
  provinceNames?: string[];
  onChange: (values: string[]) => void;
}) {
  const [keyword, setKeyword] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const { options, loading } = useItineraryListCandidateSearch(
    kind,
    keyword,
    values,
    provinceNames,
    dropdownOpen,
  );
  const ignoredStayKeyword = kind === "stay_area" && isIgnoredItineraryStayName(keyword);
  const visibleOptions = kind === "stay_area"
    ? options.filter((option) => !isIgnoredItineraryStayName(option.value))
    : options;
  const searchPlaceholder = {
    major: "输入关键词搜索专业",
    province: "输入关键词搜索省份",
    point: "输入关键词搜索考察点",
    stay_area: "输入关键词搜索住宿点",
  }[kind];

  return (
    <Select
      aria-label={label}
      allowClear
      allowCreate={kind === "major" || kind === "point"}
      showSearch
      filterOption={false}
      loading={loading}
      mode="multiple"
      maxTagCount={2}
      triggerProps={kind === "point" ? {
        autoAlignPopupWidth: false,
        autoAlignPopupMinWidth: true,
        popupStyle: { maxWidth: "calc(100vw - 32px)" },
      } : undefined}
      dropdownMenuClassName="itinerary-list-select-popup"
      dropdownMenuStyle={
        kind === "province"
          ? { minWidth: 220 }
          : kind === "point"
            ? { maxWidth: "calc(100vw - 32px)" }
            : undefined
      }
      placeholder={searchPlaceholder || placeholder}
      value={values}
      options={visibleOptions}
      notFoundContent={
        ignoredStayKeyword
          ? "该名称不是有效住宿点"
          : kind === "province" || kind === "stay_area"
            ? "未找到匹配候选"
            : "未找到匹配候选，可回车创建"
      }
      onVisibleChange={setDropdownOpen}
      onSearch={setKeyword}
      onChange={(nextValues) => onChange((nextValues as string[]).filter((value) => (
        kind !== "stay_area" || !isIgnoredItineraryStayName(value)
      )))}
    />
  );
}

type ColumnOptions = Pick<Props,
  | "onPreview"
  | "canQuote"
  | "onQuote"
  | "canCustomerQuote"
  | "canCreateCustomerQuote"
  | "onCustomerQuote"
  | "canOrderHandover"
  | "canTicketRefund"
  | "onTicketRefund"
  | "canFulfillment"
  | "onFulfillment"
  | "canEdit"
  | "onEdit"
  | "canPreview"
  | "canExport"
> & {
  onDownloadPdf: (itineraryId: string, includeDayImages: boolean) => Promise<void>;
  canNotifySales: boolean;
  onNotifySales: (projectId: string, itineraryId: string) => Promise<ItinerarySalesNotificationResult>;
  pdfDownloadingId?: string;
  listState: ItineraryListNavigationState;
  sortBy: ItineraryListSortBy;
  sortOrder: ItineraryListSortOrder;
  onSort: (sortBy: ItineraryListSortBy, sortOrder: ItineraryListSortOrder) => void;
  expandedQuoteRowKeys: string[];
  onToggleFinalizedBudgetQuoteRow: (item: ItineraryListItem) => void;
  onOpenQuoteBudget: (item: ItineraryListItem) => void;
  budgetEntryItineraryId: string | null;
};

/** 构建课程线路列表列配置。 */
function buildItineraryColumns(options: ColumnOptions): TableColumnProps<ItineraryListItem>[] {
  const columns: TableColumnProps<ItineraryListItem>[] = [
    {
      title: "团号",
      width: 168,
      render: (_, item) => (
        <ItineraryTeamCell item={item} canOrderHandover={options.canOrderHandover} />
      ),
    },
    {
      title: "标题",
      width: 320,
      render: (_, item) => <ItineraryTitleCell item={item} options={options} />,
    },
    {
      title: "状态",
      width: 92,
      render: (_, item) => {
        const status = resolveItineraryStatus(item.status);
        return <Tag color={status.color}>{status.label}</Tag>;
      },
    },
    {
      title: "核价 / 报价",
      width: 160,
      align: "center",
      render: (_, item) => <QuoteSummaryCell item={item} options={options} />,
    },
    { title: "专业", width: 170, render: (_, item) => <MultiValue values={item.majorNames} /> },
    {
      title: "销售",
      width: 120,
      render: (_, item) => (
        <ItinerarySalesCell
          item={item}
          canNotifySales={options.canNotifySales}
          onNotifySales={options.onNotifySales}
        />
      ),
    },
    {
      title: "计调",
      width: 120,
      render: (_, item) => {
        const name = item.dispatcherUserName?.trim();
        return name ? <span title={name}>{name}</span> : <span>--</span>;
      },
    },
    { title: "天数", width: 90, render: (_, item) => item.durationDays ? `${item.durationDays} 天` : "--" },
    { title: "大区域", width: 150, render: (_, item) => <MultiValue values={item.regionLabels} /> },
    { title: "考察点", width: 220, render: (_, item) => <MultiValue values={item.pointNames} /> },
    { title: "住宿点", width: 180, render: (_, item) => <MultiValue values={item.stayAreaNames} /> },
    {
      title: (
        <SortableHeader
          label="新增时间"
          field="created_at"
          activeField={options.sortBy}
          activeDirection={options.sortOrder}
          onSort={options.onSort}
        />
      ),
      dataIndex: "createdAt",
      width: 168,
      render: (_, item) => formatItineraryCreatedAt(item.createdAt),
    },
    {
      title: (
        <SortableHeader
          label="修改时间"
          field="updated_at"
          activeField={options.sortBy}
          activeDirection={options.sortOrder}
          onSort={options.onSort}
        />
      ),
      dataIndex: "updatedAt",
      width: 168,
      render: (_, item) => formatItineraryCreatedAt(item.updatedAt),
    },
    {
      title: "操作",
      fixed: "right",
      width: 220,
      render: (_, item) => (
        <div className="itinerary-list-actions">
          {options.canTicketRefund && item.teamId ? <Button type="text" size="small" onClick={() => options.onTicketRefund?.(item.teamId!)}>退票</Button> : null}
          {options.canEdit ? (
            <Button
              type="text"
              size="small"
              icon={<Pencil size={15} />}
              aria-label="编辑"
              title={["confirmed", "archived"].includes(item.status) ? "当前状态不可编辑" : "编辑"}
              disabled={["confirmed", "archived"].includes(item.status)}
              onClick={() => options.onEdit(item.itineraryId, options.listState)}
            >
              编辑
            </Button>
          ) : null}
          {options.canFulfillment ? (
            <Button
              type="text"
              size="small"
              icon={<ClipboardCheck size={15} />}
              aria-label="确认件"
              title="确认件"
              onClick={() => options.onFulfillment(item.itineraryId)}
            >
              确认件
            </Button>
          ) : null}
        </div>
      ),
    },
  ];
  return columns.filter((column) => column.title !== "操作" || options.listState.scope === "mine" || (options.listState.scope === "mine_formed" && Boolean(options.canTicketRefund)));
}

/** 销售列：点击销售姓名后确认是否发送课程线路站内及微信通知。 */
function ItinerarySalesCell({
  item,
  canNotifySales,
  onNotifySales,
}: {
  item: ItineraryListItem;
  canNotifySales: boolean;
  onNotifySales: (projectId: string, itineraryId: string) => Promise<ItinerarySalesNotificationResult>;
}) {
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const salesName = item.salesUserName?.trim();
  if (!salesName) return <span>--</span>;
  if (!canNotifySales || !item.projectId) {
    return <span title={salesName}>{salesName}</span>;
  }
  return (
    <>
      <Button
        type="text"
        size="small"
        className="itinerary-sales-notify-button"
        aria-label={`推送给销售${salesName}`}
        title={`推送给销售${salesName}`}
        onClick={() => setConfirmVisible(true)}
      >
        {salesName}
      </Button>
      <Modal
        visible={confirmVisible}
        title="确认推送给销售"
        okText="是"
        cancelText="否"
        confirmLoading={submitting}
        maskClosable={!submitting}
        escToExit={!submitting}
        onCancel={() => {
          if (!submitting) setConfirmVisible(false);
        }}
        onOk={async () => {
          setSubmitting(true);
          try {
            const result = await onNotifySales(item.projectId as string, item.itineraryId);
            if (result.sentCount > 0 && result.failureMessages.length > 0) {
              Message.warning(result.failureMessages[0]);
            } else if (result.sentCount > 0) {
              Message.success(`已发送站内及微信通知给销售${salesName}`);
            } else {
              Message.warning(result.failureMessages[0] || "通知未发送，请检查定制关联的销售账号");
            }
            setConfirmVisible(false);
          } catch {
            Message.error("销售通知发送失败，请稍后重试");
            setConfirmVisible(false);
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {`是否将线路推送给“${salesName}”？`}
      </Modal>
    </>
  );
}

/** CRM/定制风格的升降序双按钮表头。 */
function SortableHeader({
  label,
  field,
  activeField,
  activeDirection,
  onSort,
}: {
  label: string;
  field: ItineraryListSortBy;
  activeField: ItineraryListSortBy;
  activeDirection: ItineraryListSortOrder;
  onSort: (field: ItineraryListSortBy, direction: ItineraryListSortOrder) => void;
}) {
  const active = activeField === field;
  return (
    <div className="itinerary-sortable-header">
      <span>{label}</span>
      <span className="itinerary-sort-actions">
        <Button
          size="mini"
          shape="circle"
          aria-label={`${label}升序`}
          type={active && activeDirection === "asc" ? "primary" : "secondary"}
          icon={<ArrowUp size={12} />}
          onClick={(event) => {
            event.stopPropagation();
            onSort(field, "asc");
          }}
        />
        <Button
          size="mini"
          shape="circle"
          aria-label={`${label}降序`}
          type={active && activeDirection === "desc" ? "primary" : "secondary"}
          icon={<ArrowDown size={12} />}
          onClick={(event) => {
            event.stopPropagation();
            onSort(field, "desc");
          }}
        />
      </span>
    </div>
  );
}

/** 团号列：始终展示团标记；已成团带团编码，未成团补弱文案；整格单行对齐。 */
function ItineraryTeamCell({
  item,
  canOrderHandover,
}: {
  item: ItineraryListItem;
  canOrderHandover: boolean;
}) {
  return (
    <div className="itinerary-team-cell itinerary-team-cell--inline">
      <span
        className={`itinerary-formed-tag ${
          item.isFormed ? "itinerary-formed-tag--active" : "itinerary-formed-tag--inactive"
        }`}
        title={item.isFormed ? "已成团" : "未成团"}
      >
        团
      </span>
      {item.teamId ? (
        canOrderHandover && item.projectId ? (
          <a
            className="itinerary-team-code"
            href={`/crm/order-handover/${encodeURIComponent(item.projectId)}`}
            target="_blank"
            rel="noreferrer"
            title={item.teamId}
          >
            {item.teamId}
          </a>
        ) : (
          <span className="itinerary-team-code" title={item.teamId}>
            {item.teamId}
          </span>
        )
      ) : (
        <span className="itinerary-team-empty">未成团</span>
      )}
    </div>
  );
}

/** 标题列：行程名 + 换行定制号；悬停展示完整行程名。 */
function ItineraryTitleCell({ item, options }: { item: ItineraryListItem; options: ColumnOptions }) {
  const projectMeta = [item.projectCode, item.demandVersionNumber].filter(Boolean).join(" · ");
  const titleText = item.title || "--";
  return (
    <div className="itinerary-title-cell">
      <Button
        type="text"
        size="small"
        className="itinerary-title-detail-button"
        aria-label="查看行程详情"
        title={!options.canPreview ? "没有行程预览权限" : item.canPreview === false ? "行程尚未开放审稿，请等待计调完成后开启审稿" : item.title || "查看行程详情"}
        disabled={!options.canPreview || item.canPreview === false}
        onClick={() => options.onPreview(item.itineraryId, options.listState.scope !== "mine")}
      >
        <span>{titleText}</span>
        <Eye size={14} aria-hidden />
      </Button>
      {projectMeta ? <span className="itinerary-project-meta">{projectMeta}</span> : null}
      {item.canPreview === false ? <span className="itinerary-project-meta">尚未开放审稿</span> : null}
    </div>
  );
}

/** 核价与最终报价摘要，布局对齐房资源计数入口。 */
function QuoteSummaryCell({ item, options }: { item: ItineraryListItem; options: ColumnOptions }) {
  const canExpandQuote = Boolean(
    options.canCustomerQuote && (item.finalizedBudgetCount > 0 || item.finalizedQuoteCount > 0),
  );
  const expanded = options.expandedQuoteRowKeys.includes(item.itineraryId);
  const quoteTitle = options.listState.scope !== "mine"
    ? "全部行程仅供预览，请在我的行程中操作"
    : !options.canCustomerQuote
    ? "无权查看定稿报价"
    : canExpandQuote
      ? "展开已定稿核价，选择对应报价"
      : "暂无已定稿报价";

  return (
    <div className="itinerary-quote-summary">
      <QuoteMetric
        label="核价定稿数"
        value={item.finalizedBudgetCount}
        title={`仅统计已定稿核价，未定稿草稿不计入；0 表示暂无定稿版本；${options.listState.scope !== "mine" ? "当前范围仅供预览，请在我的行程中操作" : item.finalizedBudgetCount > 0 ? "点击进入最新定稿核价" : "点击进入核价"}`}
        clickable={options.canQuote && options.budgetEntryItineraryId !== item.itineraryId}
        onClick={() => options.onOpenQuoteBudget(item)}
      />
      <QuoteMetric
        label="报价定稿数"
        value={item.finalizedQuoteCount}
        title={`仅统计已定稿报价，未定稿草稿不计入；0 表示暂无定稿版本；${quoteTitle}`}
        clickable={canExpandQuote}
        expanded={expanded}
        onClick={() => options.onToggleFinalizedBudgetQuoteRow(item)}
      />
    </div>
  );
}

/** 整行展开的定稿核价表格，不改变原列表列布局。 */
function FinalizedBudgetQuoteExpandedRow({
  state,
  canCustomerQuote,
  canCreateCustomerQuote,
  onCustomerQuote,
}: {
  state: FinalizedBudgetQuoteVersionState;
  canCustomerQuote: boolean;
  canCreateCustomerQuote: boolean;
  onCustomerQuote: (budgetOrderId: string, customerQuoteId?: string) => void;
}) {
  return (
    <div className="itinerary-quote-version-list" role="region" aria-label="已定稿核价列表">
      {state.loading ? <span className="itinerary-quote-version-list__state">正在加载…</span> : null}
      {state.errorMessage ? (
        <span className="itinerary-quote-version-list__state is-error">{state.errorMessage}</span>
      ) : null}
      {!state.loading && !state.errorMessage && state.versions?.length === 0 ? (
        <span className="itinerary-quote-version-list__state">暂无已定稿核价</span>
      ) : null}
      {!state.loading && !state.errorMessage && state.versions && state.versions.length > 0 ? (
        <table className="itinerary-quote-version-table">
          <caption>已定稿核价</caption>
          <thead>
            <tr>
              <th scope="col">核价版本</th>
              <th scope="col">核价金额</th>
              <th scope="col">操作</th>
            </tr>
          </thead>
          <tbody>
            {state.versions.map((version) => {
              const canOpenVersion = Boolean(
                version.customerQuoteId ? canCustomerQuote : canCreateCustomerQuote,
              );
              return (
                <tr key={version.budgetOrderId}>
                  <td>
                    <Space size={8}>
                      <span>V{version.versionNo}</span>
                      <Tag size="small" color={version.customerQuoteId ? "green" : "orange"}>
                        {version.customerQuoteId ? "报价已定稿" : "报价未定稿"}
                      </Tag>
                    </Space>
                  </td>
                  <td>核价金额 {formatQuoteAmount(version.costBudgetTotalAmount)}</td>
                  <td>
                    {canOpenVersion ? (
                      <Button
                        type="text"
                        size="mini"
                        className="itinerary-quote-version-table__action"
                        aria-label={`进入 V${version.versionNo} 报价`}
                        onClick={() => onCustomerQuote(version.budgetOrderId, version.customerQuoteId)}
                      >
                        进入报价
                      </Button>
                    ) : (
                      <span className="itinerary-quote-version-list__state">无报价权限</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : null}
    </div>
  );
}

/** 房资源风格的上下标签计数入口。 */
function QuoteMetric({
  label,
  value,
  title,
  clickable,
  expanded,
  onClick,
}: {
  label: string;
  value: number;
  title: string;
  clickable: boolean;
  expanded?: boolean;
  onClick: () => void;
}) {
  const ariaLabel = `${label} ${value}`;
  if (clickable) {
    return (
      <button
        type="button"
        className="itinerary-quote-metric"
        aria-label={ariaLabel}
        aria-expanded={expanded}
        title={title}
        onClick={onClick}
      >
        <span>{label}</span>
        <strong>{value}</strong>
      </button>
    );
  }
  return (
    <div
      className="itinerary-quote-metric itinerary-quote-metric--static"
      title={title}
      aria-label={ariaLabel}
    >
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

/** 展示报价金额；没有最终报价金额时使用明确占位。 */
function formatQuoteAmount(value?: string): string {
  if (!value) return "--";
  const amount = Number(value);
  if (!Number.isFinite(amount)) return value;
  return `¥${amount.toLocaleString("zh-CN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** 展示多值文本。 */
function MultiValue({ values }: { values: string[] }) {
  return <LongText value={values.length > 0 ? values.join("、") : undefined} />;
}

/** 展示可截断长文本并提供完整提示。 */
function LongText({ value }: { value?: string }) {
  const text = value || "--";
  return (
    <Tooltip content={text} position="top">
      <span className="itinerary-long-text">{text}</span>
    </Tooltip>
  );
}

type ItineraryListRuntime = ReturnType<typeof useItineraryListRuntime>;

/** 统一封装移动断点监听，与商机/联系人列表工作台保持同一阈值。 */
function useIsMobileViewport(maxWidth = 760) {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return false;
    }
    return window.matchMedia(`(max-width: ${maxWidth}px)`).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return;
    }
    const mediaQuery = window.matchMedia(`(max-width: ${maxWidth}px)`);
    const sync = () => setIsMobile(mediaQuery.matches);
    sync();
    mediaQuery.addEventListener?.("change", sync);
    return () => mediaQuery.removeEventListener?.("change", sync);
  }, [maxWidth]);

  return isMobile;
}

const MOBILE_ITINERARY_SORT_OPTIONS = [
  { label: "创建时间", field: "created_at" as const },
  { label: "修改时间", field: "updated_at" as const },
];

/** 移动端筛选：关键词 + 我的/近三年范围。 */
function MobileItineraryFilterBar({ runtime }: { runtime: ItineraryListRuntime }) {
  return (
    <section
      className="itinerary-mobile-filter-panel"
      aria-label="课程线路移动端筛选"
    >
      <div className="itinerary-mobile-toolbar">
        <h1>课程线路</h1>
        <span className="itinerary-mobile-statline">
          {runtime.loading && runtime.total === 0 ? "加载中" : `共 ${runtime.total} 条`}
        </span>
      </div>
      <Radio.Group
        type="button"
        className="itinerary-mobile-scope"
        aria-label="行程范围"
        value={runtime.scope}
        onChange={(value) => runtime.handleScopeChange(value as ItineraryScope)}
      >
        <Radio value="mine">我的行程</Radio>
        <Radio value="recent_3y">近三年行程</Radio>
      </Radio.Group>
      <div className="itinerary-mobile-filter-fields">
        <Input
          allowClear
          aria-label="关键词筛选"
          value={runtime.draftKeyword}
          maxLength={{ length: 100, errorOnly: true }}
          placeholder="标题、定制号或团编码"
          onChange={runtime.setDraftKeyword}
          onPressEnter={runtime.handleSearch}
        />
        <div className="itinerary-mobile-filter-actions">
          <Button size="small" onClick={runtime.handleReset}>重置</Button>
          <Button
            type="primary"
            size="small"
            icon={<Search size={14} />}
            loading={runtime.loading}
            disabled={runtime.loading}
            onClick={runtime.handleSearch}
          >
            查询
          </Button>
        </div>
      </div>
    </section>
  );
}

/** 移动端排序条：创建时间 / 修改时间，交互对齐桌面双圆钮。 */
function MobileItinerarySortBar({
  sortBy,
  sortOrder,
  onSort,
}: {
  sortBy: ItineraryListSortBy;
  sortOrder: ItineraryListSortOrder;
  onSort: (sortBy: ItineraryListSortBy, sortOrder: ItineraryListSortOrder) => void;
}) {
  return (
    <nav
      className="itinerary-mobile-sort-bar"
      aria-label="行程排序"
      data-testid="itinerary-mobile-sort-bar"
    >
      {MOBILE_ITINERARY_SORT_OPTIONS.map((option) => (
        <SortableHeader
          key={option.field}
          label={option.label}
          field={option.field}
          activeField={sortBy}
          activeDirection={sortOrder}
          onSort={onSort}
        />
      ))}
    </nav>
  );
}

/** 移动端课程线路卡片列表；核价/报价只读，点标题看行程。 */
function MobileItineraryList({
  runtime,
  onPreview,
}: {
  runtime: ItineraryListRuntime;
  onPreview: (itineraryId: string) => void;
}) {
  return (
    <div className="itinerary-mobile-list" data-testid="itinerary-mobile-list">
      {runtime.items.length > 0 ? (
        <Spin loading={runtime.loading}>
          <div className="itinerary-mobile-card-list" role="list">
            {runtime.items.map((item) => (
              <MobileItineraryCard
                key={item.itineraryId}
                item={item}
                onPreview={onPreview}
              />
            ))}
          </div>
        </Spin>
      ) : runtime.loading ? (
        <div
          className="itinerary-mobile-list-status"
          data-testid="itinerary-mobile-list-loading"
          role="status"
          aria-live="polite"
          aria-label="正在加载课程线路"
        >
          <Spin tip="正在加载课程线路" />
        </div>
      ) : (
        <Empty description="暂无行程" />
      )}
      {runtime.loading && runtime.items.length === 0 ? null : (
        <Pagination
          className="itinerary-mobile-pagination"
          total={runtime.total}
          current={runtime.page}
          pageSize={runtime.pageSize}
          showTotal={(total) => `共 ${total} 条`}
          sizeCanChange={false}
          bufferSize={1}
          onChange={runtime.handlePageChange}
        />
      )}
    </div>
  );
}

/** 移动端行程卡片：团号、标题、状态、核价/报价、专业、销售、计调、创建/修改时间。 */
function MobileItineraryCard({
  item,
  onPreview,
}: {
  item: ItineraryListItem;
  onPreview: (itineraryId: string) => void;
}) {
  const status = resolveItineraryStatus(item.status);
  const majorText = item.majorNames.length > 0 ? item.majorNames.join("、") : "";
  const metaParts = [item.projectCode, item.demandVersionNumber, majorText].filter(Boolean);
  const salesName = item.salesUserName?.trim() || "--";
  const dispatcherName = item.dispatcherUserName?.trim() || "--";
  const previewDisabled = item.canPreview === false;
  const createdAtText = formatItineraryCreatedAt(item.createdAt);
  const updatedAtText = formatItineraryCreatedAt(item.updatedAt);

  return (
    <article className="itinerary-mobile-card" role="listitem">
      <div className="itinerary-mobile-card-top">
        <div className="itinerary-mobile-team">
          <span
            className={`itinerary-formed-tag ${
              item.isFormed ? "itinerary-formed-tag--active" : "itinerary-formed-tag--inactive"
            }`}
            title={item.isFormed ? "已成团" : "未成团"}
          >
            团
          </span>
          {item.teamId ? (
            <span className="itinerary-mobile-team-code" title={item.teamId}>{item.teamId}</span>
          ) : null}
        </div>
        <Tag color={status.color} size="small">{status.label}</Tag>
      </div>

      <button
        type="button"
        className="itinerary-mobile-card-title"
        aria-label="查看行程详情"
        title={previewDisabled ? "行程尚未开放审稿，请等待计调完成后开启审稿" : item.title || "查看行程详情"}
        disabled={previewDisabled}
        onClick={() => onPreview(item.itineraryId)}
      >
        <span>{item.title || "--"}</span>
        <Eye size={15} aria-hidden />
      </button>
      {metaParts.length > 0 ? (
        <span className="itinerary-mobile-card-meta" title={metaParts.join(" · ")}>
          {metaParts.join(" · ")}
        </span>
      ) : null}
      {previewDisabled ? <span className="itinerary-mobile-card-meta">尚未开放审稿</span> : null}

      <div className="itinerary-mobile-quote-row" aria-label="核价与报价">
        <span
          className="itinerary-mobile-quote-chip"
          aria-label={`核价定稿数 ${item.finalizedBudgetCount}`}
          title="仅统计已定稿核价，移动端只读展示"
        >
          核价 <strong>{item.finalizedBudgetCount}</strong>
        </span>
        <span
          className="itinerary-mobile-quote-chip"
          aria-label={`报价定稿数 ${item.finalizedQuoteCount}`}
          title="仅统计已定稿报价，移动端只读展示"
        >
          报价 <strong>{item.finalizedQuoteCount}</strong>
        </span>
      </div>

      <div className="itinerary-mobile-card-people" title={`销售 ${salesName} · 计调 ${dispatcherName}`}>
        <span><em>销售</em> {salesName}</span>
        <span><em>计调</em> {dispatcherName}</span>
      </div>
      <div className="itinerary-mobile-card-times" aria-label="创建与修改时间">
        <span title={`创建时间 ${createdAtText}`}>
          <em>创建</em>
          <time dateTime={item.createdAt}>{createdAtText}</time>
        </span>
        <span title={`修改时间 ${updatedAtText}`}>
          <em>修改</em>
          <time dateTime={item.updatedAt}>{updatedAtText}</time>
        </span>
      </div>
    </article>
  );
}
